import { Check, Download, Eye, FileText, Pencil, Plus, RotateCcw, Trash2, Upload } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, apiDownload, apiRequest } from "../api";
import { useAuth } from "../auth";
import { ErrorState, LoadingState } from "../components/PageState";
import { matchesQuickSearch, QuickSearch } from "../components/QuickSearch";
import { SearchSelect } from "../components/SearchSelect";
import { formatDate, formatMoney, Message, Modal, PageHeader, StatusBadge, Tabs, today } from "../components/Ui";
import type {
  CommercialDocumentRow,
  CommercialDocumentType,
  FoundationData,
  ItemListRow,
  PartnerRow
} from "../types";

const tabs = [
  { value: "Quotation", label: "Báo giá" },
  { value: "Contract", label: "Hợp đồng" },
  { value: "SalesOrder", label: "Đơn hàng" },
  { value: "PurchaseOrder", label: "Đơn mua" }
];

const titleByType: Record<CommercialDocumentType, string> = {
  Quotation: "Báo giá",
  Contract: "Hợp đồng",
  SalesOrder: "Đơn hàng",
  PurchaseOrder: "Đơn mua"
};

type DraftLine = {
  clientKey: string;
  itemId: string;
  uomId: string;
  quantity: number;
  unitPrice: number;
  customerUnitPriceAfterVat: number | null;
  vatPercent: number;
  notes: string;
  customerItemCode: string;
  customerItemName: string;
  customerIdentityFromPo: boolean;
  allocations: DraftAllocation[];
};

type DraftAllocation = {
  clientKey: string;
  itemId: string;
  uomId: string;
  quantity: number;
  customerVariantName: string;
};

let draftLineSequence = 0;
const nextDraftLineKey = () => `commercial-line-${Date.now()}-${++draftLineSequence}`;

type Detail = CommercialDocumentRow & {
  partnerId: string;
  notes?: string;
  lines: Array<{
    id: string;
    lineNo: number;
    itemId?: string | null;
    uomId: string;
    itemCode: string;
    itemName: string;
    internalItemCode?: string | null;
    internalItemName?: string | null;
    customerItemCode?: string | null;
    customerItemName?: string | null;
    uomName: string;
    quantity: number;
    quantityIssued?: number;
    quantityReceived?: number;
    quantityProcessed?: number;
    quantityRemaining?: number;
    unitPrice: number;
    customerUnitPriceAfterVat?: number | null;
    vatPercent: number;
    lineTotal: number;
    allocations: Array<{
      id: string;
      itemId: string;
      itemCode: string;
      itemName: string;
      uomId: string;
      uomName: string;
      quantity: number;
      quantityProcessed?: number;
      quantityRemaining?: number;
      customerVariantName: string;
    }>;
  }>;
  deliveryAddress?: string | null;
  deliveryContactName?: string | null;
  deliveryPhone?: string | null;
  sourceDocumentId?: string | null;
  linkedInventoryDocuments?: Array<{
    id: string;
    number: string;
    documentDate: string;
    status: "Draft" | "Completed" | "Canceled";
    deliveryNote?: { id: string; number: string; status: "Draft" | "Completed" | "Canceled" } | null;
  }>;
};

type CustomerPoPreview = {
  fileName: string;
  uploadToken: string;
  poNumber: string;
  documentDate: string;
  duplicate: boolean;
  customer: {
    name?: string | null;
    taxCode?: string | null;
    address?: string | null;
    matchedPartnerId?: string | null;
    matchedPartnerLabel?: string | null;
    confidence: "High" | "Missing";
  };
  delivery?: {
    address?: string | null;
    contactName?: string | null;
    phone?: string | null;
  };
  lines: Array<{
    customerItemCode: string;
    customerItemName: string;
    uomText: string;
    orderedQuantity: number;
    giftQuantity: number;
    totalQuantity: number;
    unitPrice: number;
    customerUnitPriceAfterVat?: number | null;
    vatPercent: number;
    matchedItemId?: string | null;
    matchedItemLabel?: string | null;
    matchedUomId?: string | null;
    matchedUomLabel?: string | null;
    matchSource?: "CustomerGroup" | "CustomerReference" | "InternalCode" | "InternalName" | "Missing";
    matchedVariants?: Array<{
      itemId: string;
      itemCode: string;
      itemName: string;
      uomId: string;
      uomName: string;
      customerVariantName: string;
    }>;
    confidence: "High" | "Medium" | "Missing";
  }>;
};

const blankLine = (): DraftLine => ({
  clientKey: nextDraftLineKey(),
  itemId: "",
  uomId: "",
  quantity: 1,
  unitPrice: 0,
  customerUnitPriceAfterVat: null,
  vatPercent: 0,
  notes: "",
  customerItemCode: "",
  customerItemName: "",
  customerIdentityFromPo: false,
  allocations: []
});

const blankAllocation = (uomId = "", quantity = 0): DraftAllocation => ({
  clientKey: nextDraftLineKey(),
  itemId: "",
  uomId,
  quantity,
  customerVariantName: ""
});

const isOrderType = (type: CommercialDocumentType) => type === "SalesOrder" || type === "PurchaseOrder";
const fulfillmentProgressClass = (ordered: number, processed: number, remaining: number) => processed <= 0
  ? "progress-none"
  : remaining > 0 && processed < ordered
    ? "progress-partial"
    : "progress-complete";
const fulfillmentLabel = (row: CommercialDocumentRow) => {
  if (row.fulfillmentStatus === "FullyIssued") return "Đã xuất đủ";
  if (row.fulfillmentStatus === "PartiallyIssued") return "Đã giao một phần";
  if (row.fulfillmentStatus === "FullyReceived") return "Đã nhập đủ";
  if (row.fulfillmentStatus === "PartiallyReceived") return "Đã nhập một phần";
  return row.documentType === "PurchaseOrder" ? "Chưa nhập kho" : "Chưa xuất kho";
};
const workflowBadge = (row: CommercialDocumentRow) => {
  if (row.workflowStatus === "PartiallyFulfilled")
    return <span className="status-pill info">{row.documentType === "PurchaseOrder" ? "Đã nhập một phần" : "Đã giao một phần"}</span>;
  return <StatusBadge status={row.workflowStatus === "InProgress" ? "InProgress" : row.status} />;
};

export function CommercialPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("ADMIN") ?? false;
  const hasPermission = (permission: string) => isAdmin || user?.permissions.includes(permission) === true;
  const availableTabs = tabs.filter((tab) => tab.value === "PurchaseOrder"
    ? hasPermission("Purchasing.View")
    : hasPermission("Sales.View"));
  const [type, setType] = useState<CommercialDocumentType>(() =>
    hasPermission("Sales.View") ? "Quotation" : "PurchaseOrder");
  const [documents, setDocuments] = useState<CommercialDocumentRow[]>([]);
  const [sourceDocuments, setSourceDocuments] = useState<CommercialDocumentRow[]>([]);
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [items, setItems] = useState<ItemListRow[]>([]);
  const [foundation, setFoundation] = useState<FoundationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [detail, setDetail] = useState<Detail | null>(null);
  const [partnerPrompt, setPartnerPrompt] = useState(false);
  const [poPreview, setPoPreview] = useState<CustomerPoPreview | null>(null);
  const [poImporting, setPoImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    number: "",
    documentDate: today(),
    dueDate: "",
    partnerId: "",
    partnerName: "",
    sourceDocumentId: "",
    internalLotCode: "",
    customerPoNumber: "",
    customerPoUploadToken: "",
    customerPoOriginalFileName: "",
    deliveryAddress: "",
    deliveryContactName: "",
    deliveryPhone: "",
    notes: "",
    lines: [blankLine()]
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [documentData, partnerData, itemData, foundationData, sourceData] = await Promise.all([
        apiRequest<CommercialDocumentRow[]>(`/api/commercial-documents?type=${type}`),
        apiRequest<PartnerRow[]>("/api/master-data/partners"),
        apiRequest<ItemListRow[]>("/api/items"),
        apiRequest<FoundationData>("/api/master-data/foundation"),
        apiRequest<CommercialDocumentRow[]>(`/api/commercial-documents/available-sources?targetType=${type}`)
      ]);
      setDocuments(documentData);
      setSourceDocuments(sourceData);
      setPartners(partnerData);
      setItems(itemData.filter((x) => x.isActive));
      setFoundation(foundationData);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được chứng từ kinh doanh.");
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredDocuments = useMemo(() => documents.filter((row) => matchesQuickSearch(
    search,
    row.number,
    row.documentDate,
    row.partner.code,
    row.partner.name,
    row.internalLotCode,
    row.status,
    row.workflowStatus,
    row.fulfillmentStatus,
    row.fulfillmentLines?.map((line) => `${line.itemCode} ${line.itemName} ${line.uomName}`).join(" ")
  )), [documents, search]);

  const partnerOptions = useMemo(
    () => partners.filter((partner) =>
      type === "PurchaseOrder"
        ? partner.partnerType === "Supplier" || partner.partnerType === "ExternalService" || partner.partnerType === "GovernmentAgency"
        : partner.partnerType === "Customer"
    ),
    [partners, type]
  );
  const selectableItems = useMemo(
    () => type === "PurchaseOrder"
      ? items.filter((item) => item.itemType !== "Product")
      : items,
    [items, type]
  );
  const partnerSearchOptions = useMemo(
    () => {
      const options = partnerOptions.map((partner) => ({
        value: partner.id,
        label: `${partner.code} – ${partner.name}`,
        keywords: `${partner.name} ${partner.code} ${partner.taxCode ?? ""}`
      }));
      if (form.partnerId && !options.some((option) => option.value === form.partnerId))
        options.unshift({ value: form.partnerId, label: form.partnerName, keywords: form.partnerName });
      return options;
    },
    [form.partnerId, form.partnerName, partnerOptions]
  );
  const sourceSearchOptions = useMemo(
    () => sourceDocuments.map((source) => ({
      value: source.id,
      label: `${source.number} – ${source.partner.name}`,
      keywords: `${source.number} ${source.partner.code} ${source.partner.name}`
    })),
    [sourceDocuments]
  );
  const itemSearchOptions = useMemo(
    () => selectableItems.map((item) => ({
      value: item.id,
      label: `${item.code} – ${item.name}`,
      selectedLabel: item.name,
      keywords: `${item.code} ${item.name} ${item.specificationSymbol ?? ""} ${(item.customerReferences ?? []).map((reference) => `${reference.customerItemCode} ${reference.customerItemName}`).join(" ")}`
    })),
    [selectableItems]
  );
  const uomSearchOptions = useMemo(() => (foundation?.uoms ?? []).map((uom) => ({
    value: uom.id, label: `${uom.code} – ${uom.name}`, selectedLabel: uom.name, keywords: `${uom.code} ${uom.name} ${uom.symbol}`
  })), [foundation]);

  const total = form.lines.reduce((sum, line) => {
    const subtotal = line.quantity * line.unitPrice;
    return sum + subtotal + subtotal * line.vatPercent / 100;
  }, 0);
  const vatTotal = form.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice * line.vatPercent / 100, 0);
  const customerReferenceFor = (itemId: string, partnerId = form.partnerId) =>
    items.find((item) => item.id === itemId)?.customerReferences?.find((reference) => reference.customerId === partnerId);
  const displayIdentityFor = (line: DraftLine) => {
    const item = items.find((row) => row.id === line.itemId);
    const reference = customerReferenceFor(line.itemId);
    return {
      code: line.customerItemCode || reference?.customerItemCode || item?.code || "",
      name: line.customerItemName || reference?.customerItemName || item?.name || "",
      hasCustomerIdentity: !!(line.customerItemCode || reference?.customerItemCode)
    };
  };

  const openCreate = () => {
    setError("");
    setMessage("");
    setEditingId("");
    setPoPreview(null);
    void apiRequest<CommercialDocumentRow[]>(
      `/api/commercial-documents/available-sources?targetType=${type}`
    ).then(setSourceDocuments).catch(() => setSourceDocuments([]));
    setForm({
      number: "",
      documentDate: today(),
      dueDate: "",
      partnerId: partnerOptions[0]?.id ?? "",
      partnerName: partnerOptions[0]?.name ?? "",
      sourceDocumentId: "",
      internalLotCode: "",
      customerPoNumber: "",
      customerPoUploadToken: "",
      customerPoOriginalFileName: "",
      deliveryAddress: "",
      deliveryContactName: "",
      deliveryPhone: "",
      notes: "",
      lines: [blankLine()]
    });
    setShowForm(true);
  };

  const openEdit = async (id: string) => {
    setError("");
    try {
      const [document, availableSources] = await Promise.all([
        apiRequest<Detail>(`/api/commercial-documents/${id}`),
        apiRequest<CommercialDocumentRow[]>(
          `/api/commercial-documents/available-sources?targetType=${type}&currentDocumentId=${id}`
        )
      ]);
      setSourceDocuments(availableSources);
      setEditingId(id);
      setPoPreview(null);
      setForm({
        number: document.number,
        documentDate: document.documentDate,
        dueDate: document.dueDate ?? "",
        partnerId: document.partnerId,
        partnerName: document.partner.name,
        sourceDocumentId: document.sourceDocumentId ?? "",
        internalLotCode: document.internalLotCode ?? "",
        customerPoNumber: document.customerPoNumber ?? "",
        customerPoUploadToken: "",
        customerPoOriginalFileName: document.customerPoAttachmentName ?? "",
        deliveryAddress: document.deliveryAddress ?? "",
        deliveryContactName: document.deliveryContactName ?? "",
        deliveryPhone: document.deliveryPhone ?? "",
        notes: document.notes ?? "",
        lines: document.lines.map((line) => ({
          clientKey: nextDraftLineKey(),
          itemId: line.itemId ?? "",
          uomId: line.uomId,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          customerUnitPriceAfterVat: line.customerUnitPriceAfterVat ?? null,
          vatPercent: line.vatPercent,
          notes: "",
          customerItemCode: line.customerItemCode ?? "",
          customerItemName: line.customerItemName ?? "",
          customerIdentityFromPo: !!document.customerPoNumber,
          allocations: (line.allocations ?? []).map((allocation) => ({
            clientKey: nextDraftLineKey(),
            itemId: allocation.itemId,
            uomId: allocation.uomId,
            quantity: allocation.quantity,
            customerVariantName: allocation.customerVariantName ?? ""
          }))
        }))
      });
      setShowForm(true);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không mở được chứng từ Nháp để sửa.");
    }
  };

  const updateLine = (index: number, update: Partial<DraftLine>) => {
    const lines = [...form.lines];
    const current = { ...lines[index], ...update };
    if (update.itemId) {
      current.uomId = items.find((x) => x.id === update.itemId)?.baseUomId ?? "";
      if (!current.customerIdentityFromPo) {
        const reference = customerReferenceFor(update.itemId);
        current.customerItemCode = reference?.customerItemCode ?? "";
        current.customerItemName = reference?.customerItemName ?? "";
      }
    }
    if (update.uomId)
      current.allocations = current.allocations.map((allocation) => ({ ...allocation, uomId: update.uomId! }));
    lines[index] = current;
    setForm({ ...form, lines });
  };

  const addAllocation = (lineIndex: number) => {
    const lines = [...form.lines];
    const line = lines[lineIndex];
    const allocated = line.allocations.reduce((sum, allocation) => sum + allocation.quantity, 0);
    lines[lineIndex] = {
      ...line,
      itemId: "",
      allocations: [...line.allocations, blankAllocation(line.uomId, Math.max(0, line.quantity - allocated))]
    };
    setForm({ ...form, lines });
  };

  const updateAllocation = (lineIndex: number, allocationIndex: number, update: Partial<DraftAllocation>) => {
    const lines = [...form.lines];
    const line = lines[lineIndex];
    const allocations = [...line.allocations];
    const current = { ...allocations[allocationIndex], ...update, uomId: line.uomId };
    if (update.itemId) {
      const item = items.find((row) => row.id === update.itemId);
      const reference = item?.customerReferences?.find((row) =>
        row.customerId === form.partnerId &&
        (!line.customerItemCode || row.customerItemCode.toLocaleLowerCase("vi-VN") === line.customerItemCode.toLocaleLowerCase("vi-VN")));
      current.customerVariantName = reference?.customerVariantName || item?.name || "";
    }
    allocations[allocationIndex] = current;
    lines[lineIndex] = { ...line, itemId: "", allocations };
    setForm({ ...form, lines });
  };

  const removeAllocation = (lineIndex: number, allocationIndex: number) => {
    const lines = [...form.lines];
    lines[lineIndex] = {
      ...lines[lineIndex],
      allocations: lines[lineIndex].allocations.filter((_, index) => index !== allocationIndex)
    };
    setForm({ ...form, lines });
  };

  const selectSource = async (id: string) => {
    if (!id) {
      setForm({ ...form, sourceDocumentId: "" });
      return;
    }
    try {
      const source = await apiRequest<Detail>(`/api/commercial-documents/${id}`);
      setForm({
        ...form,
        sourceDocumentId: id,
        partnerId: source.partnerId,
        partnerName: source.partner.name,
        deliveryAddress: source.deliveryAddress ?? form.deliveryAddress,
        deliveryContactName: source.deliveryContactName ?? form.deliveryContactName,
        deliveryPhone: source.deliveryPhone ?? form.deliveryPhone,
        lines: source.lines.map((line) => ({
          clientKey: nextDraftLineKey(),
          itemId: line.itemId ?? "",
          uomId: line.uomId,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          customerUnitPriceAfterVat: line.customerUnitPriceAfterVat ?? null,
          vatPercent: line.vatPercent,
          notes: "",
          customerItemCode: line.customerItemCode ?? "",
          customerItemName: line.customerItemName ?? "",
          customerIdentityFromPo: type === "SalesOrder" && !line.itemId,
          allocations: (line.allocations ?? []).map((allocation) => ({
            clientKey: nextDraftLineKey(),
            itemId: allocation.itemId,
            uomId: allocation.uomId,
            quantity: allocation.quantity,
            customerVariantName: allocation.customerVariantName ?? ""
          }))
        }))
      });
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lấy được dữ liệu từ chứng từ nguồn.");
    }
  };

  const importCustomerPo = async (file: File) => {
    setError("");
    setPoImporting(true);
    const body = new FormData();
    body.append("file", file);
    try {
      setPoPreview(await apiRequest<CustomerPoPreview>(
        "/api/commercial-documents/import-customer-po-preview",
        { method: "POST", body }
      ));
    } catch (reason) {
      setPoPreview(null);
      setError(reason instanceof ApiError ? reason.message : "Không đọc được PO khách hàng.");
    } finally {
      setPoImporting(false);
    }
  };

  const applyCustomerPo = () => {
    if (!poPreview || poPreview.duplicate) return;
    const poLines = poPreview.lines.map((line) => {
      const totalQuantity = line.totalQuantity || line.orderedQuantity + line.giftQuantity;
      return {
        ...blankLine(),
        // Dòng PO của khách luôn được giữ độc lập. Danh mục khớp chỉ dùng làm
        // gợi ý; người dùng chủ động phân bổ mã nội bộ ở Đơn hàng hoặc Phiếu xuất.
        itemId: "",
        uomId: line.matchedUomId ?? "",
        quantity: totalQuantity,
        unitPrice: line.unitPrice,
        customerUnitPriceAfterVat: line.customerUnitPriceAfterVat ?? null,
        vatPercent: line.vatPercent,
        notes: `SL đặt: ${line.orderedQuantity}; SL tặng: ${line.giftQuantity}`,
        customerItemCode: line.customerItemCode,
        customerItemName: line.customerItemName,
        customerIdentityFromPo: true
      };
    });
    setForm({
      ...form,
      documentDate: poPreview.documentDate || form.documentDate,
      partnerId: poPreview.customer.matchedPartnerId ?? "",
      partnerName: poPreview.customer.name ?? "",
      customerPoNumber: poPreview.poNumber,
      customerPoUploadToken: poPreview.uploadToken,
      customerPoOriginalFileName: poPreview.fileName,
      deliveryAddress: poPreview.delivery?.address ?? "",
      deliveryContactName: poPreview.delivery?.contactName ?? "",
      deliveryPhone: poPreview.delivery?.phone ?? "",
      notes: [form.notes, `Nhập từ PO khách hàng ${poPreview.poNumber} (${poPreview.fileName}).`]
        .filter(Boolean).join("\n"),
      lines: poLines.length ? poLines : [blankLine()]
    });
    setMessage("Đã giữ nguyên các dòng PO của khách trong Đơn hàng Nháp. Có thể phân bổ mã nội bộ tại đây hoặc khi tạo Phiếu xuất kho.");
  };

  const persist = async (savePartnerToCatalog: boolean) => {
    setError("");
    try {
      await apiRequest(editingId ? `/api/commercial-documents/${editingId}` : "/api/commercial-documents", {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify({
          ...form,
          number: form.number || null,
          documentType: type,
          partnerId: form.partnerId || null,
          partnerName: form.partnerName.trim() || null,
          savePartnerToCatalog,
          dueDate: form.dueDate || null,
          sourceDocumentId: form.sourceDocumentId || null,
          lines: form.lines.map(({ clientKey: _clientKey, customerIdentityFromPo: _customerIdentityFromPo, allocations, ...line }) => ({
            ...line,
            itemId: line.itemId || null,
            allocations: allocations.map(({ clientKey: _allocationKey, ...allocation }) => allocation)
          }))
        })
      });
      setShowForm(false);
      setMessage(editingId
        ? `Đã cập nhật ${titleByType[type].toLowerCase()} Nháp.`
        : `Đã tạo ${titleByType[type].toLowerCase()} ở trạng thái Nháp.`);
      setEditingId("");
      setPartnerPrompt(false);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được chứng từ.");
    }
  };

  const save = async () => {
    if (!form.partnerId && (type === "SalesOrder" || type === "PurchaseOrder") && form.partnerName.trim()) {
      setPartnerPrompt(true);
      return;
    }
    await persist(false);
  };

  const action = async (id: string, operation: "complete" | "cancel") => {
    const label = operation === "complete" ? (isOrderType(type) ? "duyệt và triển khai" : "hoàn tất") : "hủy";
    if (!window.confirm(`Xác nhận ${label} chứng từ này?`)) return;
    setError("");
    try {
      await apiRequest(`/api/commercial-documents/${id}/${operation}`, { method: "POST" });
      setMessage(operation === "complete"
        ? (isOrderType(type) ? "Đơn đã duyệt và chuyển sang Đang thực hiện." : "Chứng từ đã hoàn tất.")
        : "Chứng từ đã chuyển sang Đã hủy.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không thực hiện được thao tác.");
    }
  };

  const view = async (id: string) => {
    try {
      setDetail(await apiRequest<Detail>(`/api/commercial-documents/${id}`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được chi tiết.");
    }
  };

  const downloadCustomerPo = async (document: Detail) => {
    try {
      const blob = await apiDownload(`/api/commercial-documents/${document.id}/customer-po`);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.customerPoAttachmentName || `${document.customerPoNumber || document.number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được tệp PO khách hàng.");
    }
  };

  if (loading && !foundation) return <LoadingState />;
  if (error && !foundation) return <ErrorState message={error} retry={load} />;

  return (
    <div className="module-page">
      <PageHeader
        eyebrow="BÁN HÀNG VÀ MUA HÀNG"
        title="Kinh doanh"
        description="Thông tin đơn vị và hàng hóa lấy trực tiếp từ Danh mục; hệ thống tự tính tiền hàng, VAT và tổng giá."
        action={hasPermission(`${type === "PurchaseOrder" ? "Purchasing" : "Sales"}.Create`) ? <button className="primary-button" onClick={openCreate}><Plus size={17} /> Tạo {titleByType[type]}</button> : undefined}
      />
      <Tabs tabs={availableTabs} value={type} onChange={(value) => { setType(value as CommercialDocumentType); setMessage(""); setSearch(""); }} />
      {message && <Message tone="success">{message}</Message>}
      {error && <Message tone="error">{error}</Message>}
      <section className="panel module-panel">
        <QuickSearch value={search} onChange={setSearch} placeholder="Tìm số chứng từ, khách hàng/NCC, mã hàng, số lô hoặc trạng thái..." resultCount={filteredDocuments.length} />
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead><tr><th className="stt-column">STT</th><th>Số chứng từ</th><th>Ngày</th><th>Khách hàng / NCC</th><th>Số lô đặt in</th><th>Giá trị hàng</th><th>VAT</th><th>Tổng giá</th>{isOrderType(type) && <th>Tình trạng kho</th>}<th>Trạng thái</th><th /></tr></thead>
            <tbody>
              {filteredDocuments.map((row, index) => (
                <tr key={row.id}>
                  <td className="stt-column">{index + 1}</td>
                  <td><strong>{row.number}</strong>{row.customerPoNumber && <small className="table-subtext">PO khách: {row.customerPoNumber}</small>}<small className="table-subtext">{row.lineCount} dòng hàng</small></td>
                  <td>{formatDate(row.documentDate)}</td>
                  <td><strong>{row.partner.name}</strong><small className="table-subtext">{row.partner.code}</small></td>
                  <td>{row.internalLotCode || "—"}</td>
                  <td className="number-cell">{formatMoney(row.subtotal)}</td>
                  <td className="number-cell">{formatMoney(row.vatAmount)}</td>
                  <td className="number-cell"><strong>{formatMoney(row.grandTotal)}</strong></td>
                  {isOrderType(type) && <td><span className={`status-pill ${row.fulfillmentStatus === "FullyIssued" || row.fulfillmentStatus === "FullyReceived" ? "success" : row.fulfillmentStatus === "PartiallyIssued" || row.fulfillmentStatus === "PartiallyReceived" ? "warning" : "danger"}`}>{fulfillmentLabel(row)}</span>{row.fulfillmentLines?.map((line, index) => <small className={`table-subtext progress-line ${fulfillmentProgressClass(line.orderedQuantity, line.processedQuantity, line.remainingQuantity)}`} key={`${line.itemCode}-${line.uomName}-${index}`}><strong>{line.itemCode}</strong>: đặt {line.orderedQuantity} · {type === "PurchaseOrder" ? "đã nhập" : "đã xuất"} {line.processedQuantity} · còn {line.remainingQuantity} {line.uomName}</small>)}</td>}
                  <td>{workflowBadge(row)}</td>
                  <td>
                    <div className="table-actions">
                      <button title="Xem" onClick={() => void view(row.id)}><Eye size={15} /></button>
                      {row.status === "Draft" && hasPermission(`${type === "PurchaseOrder" ? "Purchasing" : "Sales"}.Edit`) && <button title="Sửa bản Nháp" onClick={() => void openEdit(row.id)}><Pencil size={15} /></button>}
                      {row.status === "Draft" && hasPermission(`${type === "PurchaseOrder" ? "Purchasing" : "Sales"}.Complete`) && <button className="success-action" title={isOrderType(type) ? "Duyệt và triển khai" : "Hoàn tất"} onClick={() => void action(row.id, "complete")}><Check size={15} /></button>}
                      {row.status !== "Canceled" && hasPermission(`${type === "PurchaseOrder" ? "Purchasing" : "Sales"}.Cancel`) && <button className="danger-action" title="Hủy" onClick={() => void action(row.id, "cancel")}><RotateCcw size={15} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredDocuments.length && <tr><td colSpan={isOrderType(type) ? 11 : 10}><div className="empty-state">Không có {titleByType[type].toLowerCase()} phù hợp bộ lọc.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {showForm && (
        <Modal title={`${editingId ? "Sửa" : "Tạo"} ${titleByType[type]}`} subtitle={editingId ? "Chứng từ đang ở trạng thái Nháp và được phép chỉnh sửa." : "Bỏ trống số chứng từ để hệ thống tự đánh số."} onClose={() => setShowForm(false)} wide error={error}>
          {type === "SalesOrder" && <section className="po-import-panel">
            <div className="po-import-heading"><div><FileText size={22} /><span><strong>Tạo đơn hàng từ PO khách hàng</strong><small>Nhận PDF có lớp chữ; luôn xem trước trước khi đưa vào Đơn hàng Nháp.</small></span></div><label className="secondary-button upload-button"><Upload size={16} /> {poImporting ? "Đang đọc PO..." : "Tải PO khách hàng"}<input type="file" accept="application/pdf,.pdf" disabled={poImporting} onChange={(event) => { const file = event.target.files?.[0]; if (file) void importCustomerPo(file); event.currentTarget.value = ""; }} /></label></div>
            {form.customerPoNumber && !poPreview && <div className="po-linked-summary"><strong>PO: {form.customerPoNumber}</strong><span>{form.customerPoOriginalFileName || "Đã lưu tệp PO"}</span></div>}
            {poPreview && <div className="po-preview">
              <div className={`po-preview-summary ${poPreview.duplicate ? "danger" : ""}`}><span><strong>{poPreview.poNumber || "Chưa nhận ra số PO"}</strong> · {formatDate(poPreview.documentDate)} · {poPreview.fileName}</span>{poPreview.duplicate && <strong>PO này đã tồn tại trong hệ thống</strong>}</div>
              <div className="po-customer-row"><span className={poPreview.customer.confidence === "High" ? "po-match-high" : "po-match-missing"}>{poPreview.customer.confidence === "High" ? "Đã khớp khách hàng" : "Chưa khớp khách hàng"}</span><strong>{poPreview.customer.matchedPartnerLabel || poPreview.customer.name || "—"}</strong><small>MST: {poPreview.customer.taxCode || "—"}</small></div>
              <div className="data-table-wrapper po-preview-table"><table className="data-table"><thead><tr><th>STT</th><th>Mã SP khách</th><th>Tên trên PO</th><th>Gợi ý danh mục ERP</th><th>ĐVT</th><th>SL đặt</th><th>SL tặng</th><th>Tổng SL</th><th>Đơn giá PO sau VAT</th><th>VAT</th></tr></thead><tbody>{poPreview.lines.map((line, index) => <tr key={`${line.customerItemCode}-${index}`}><td>{index + 1}</td><td><strong>{line.customerItemCode}</strong></td><td>{line.customerItemName}</td><td><span className={line.confidence === "High" ? "po-match-high" : line.confidence === "Medium" ? "po-match-medium" : "po-match-missing"}>{line.matchSource === "CustomerGroup" ? "Mã hàng chung" : line.confidence === "High" ? "Có gợi ý" : line.confidence === "Medium" ? "Cần kiểm tra" : "Chưa có gợi ý"}</span><small className="table-subtext">{line.matchSource === "CustomerGroup" ? `${line.matchedVariants?.length ?? 0} sản phẩm nội bộ có thể phân bổ` : line.matchedItemLabel ? `${line.matchedItemLabel} · không tự chọn` : "Phân bổ mã nội bộ sau khi áp dụng"}</small></td><td>{line.matchedUomLabel || line.uomText}</td><td>{line.orderedQuantity}</td><td>{line.giftQuantity}</td><td><strong>{line.totalQuantity}</strong></td><td className="number-cell"><strong>{formatMoney(line.customerUnitPriceAfterVat ?? line.unitPrice)}</strong><small className="table-subtext">ERP trước VAT: {formatMoney(line.unitPrice)}</small></td><td>{line.vatPercent}%</td></tr>)}</tbody></table></div>
              <div className="form-actions compact"><button className="primary-button" disabled={poPreview.duplicate} onClick={applyCustomerPo}><Check size={16} /> Áp dụng vào Đơn hàng Nháp</button></div>
            </div>}
          </section>}
          <div className="form-grid three">
            <label>Số chứng từ<input disabled={!!editingId} value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="Tự động" /></label>
            <label>Ngày *<input type="date" value={form.documentDate} onChange={(e) => setForm({ ...form, documentDate: e.target.value })} /></label>
            <label>Hạn thanh toán<input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} /></label>
            <label className="span-two">Khách hàng / Nhà cung cấp *
              <SearchSelect
                value={form.partnerId}
                options={partnerSearchOptions}
                onChange={(partnerId) => setForm({
                  ...form,
                  partnerId,
                  partnerName: partnerOptions.find((partner) => partner.id === partnerId)?.name ?? form.partnerName,
                  lines: form.lines.map((line) => {
                    if (line.customerIdentityFromPo) return line;
                    const reference = items.find((item) => item.id === line.itemId)?.customerReferences?.find((row) => row.customerId === partnerId);
                    return {
                      ...line,
                      customerItemCode: reference?.customerItemCode ?? "",
                      customerItemName: reference?.customerItemName ?? ""
                    };
                  })
                })}
                onQueryChange={(partnerName) => setForm({ ...form, partnerId: "", partnerName })}
                placeholder="Nhập mã hoặc tên khách hàng/NCC"
              />
            </label>
            {(type === "Contract" || type === "SalesOrder") && <label>Tạo từ báo giá / hợp đồng<SearchSelect value={form.sourceDocumentId} options={sourceSearchOptions} onChange={(value) => void selectSource(value)} placeholder="Nhập số chứng từ hoặc tên khách hàng" emptyText="Không còn chứng từ đủ điều kiện liên kết" /></label>}
            {type === "PurchaseOrder" && <label>Số lô in sẵn trên hộp<input value={form.internalLotCode} onChange={(e) => setForm({ ...form, internalLotCode: e.target.value })} placeholder="Nhập khi đặt hộp" /></label>}
          </div>
          {type === "SalesOrder" && <div className="form-grid three delivery-fields">
            <label className="span-two">Địa chỉ giao hàng theo PO<input value={form.deliveryAddress} onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })} placeholder="Lấy nguyên văn từ PO hoặc nhập tay" /></label>
            <label>Người nhận<input value={form.deliveryContactName} onChange={(e) => setForm({ ...form, deliveryContactName: e.target.value })} /></label>
            <label>Số điện thoại người nhận<input value={form.deliveryPhone} onChange={(e) => setForm({ ...form, deliveryPhone: e.target.value })} /></label>
          </div>}
          <div className="line-editor">
            <div className="line-editor-header"><strong>Danh sách hàng hóa</strong></div>
            <div className="data-table-wrapper">
              <table className="data-table line-table commercial-line-table">
                <thead><tr><th>STT</th><th>Tên SP / nguyên liệu</th><th>Đơn vị</th><th>Số lượng</th><th>Đơn giá</th><th>VAT</th><th>Thành giá</th><th /></tr></thead>
                <tbody>
                  {form.lines.map((line, index) => {
                    const subtotal = line.quantity * line.unitPrice;
                    const lineTotal = subtotal + subtotal * line.vatPercent / 100;
                    const allocated = line.allocations.reduce((sum, allocation) => sum + allocation.quantity, 0);
                    const allocationComplete = line.allocations.length > 0 && Math.abs(allocated - line.quantity) < 0.000001;
                    return (
                      <Fragment key={line.clientKey}>
                      <tr>
                        <td>{index + 1}</td>
                        <td>{line.customerIdentityFromPo
                          ? <div className="po-order-line"><strong>{line.customerItemCode}</strong><span>{line.customerItemName}</span><small>{line.allocations.length ? `Đã phân bổ ${line.allocations.length} mã nội bộ` : "Chưa phân bổ mã nội bộ"}</small></div>
                          : <><SearchSelect value={line.itemId} options={itemSearchOptions} onChange={(itemId) => updateLine(index, { itemId })} placeholder="Nhập mã hoặc tên hàng" />{line.itemId && (() => { const identity = displayIdentityFor(line); return <small className="table-subtext customer-line-identity">{identity.hasCustomerIdentity ? `KH: ${identity.code} – ${identity.name}` : "Khách hàng chưa có mã/tên riêng – dùng mã/tên nội bộ"}</small>; })()}</>}</td>
                        <td><SearchSelect value={line.uomId} options={uomSearchOptions} onChange={(uomId) => updateLine(index, { uomId })} placeholder="Tìm đơn vị" /></td>
                        <td><input type="number" min="0" step="any" value={line.quantity} onChange={(e) => updateLine(index, { quantity: Number(e.target.value) })} /></td>
                        <td><input type="number" min="0" step="any" value={line.unitPrice} onChange={(e) => updateLine(index, { unitPrice: Number(e.target.value), customerUnitPriceAfterVat: null })} />{line.customerUnitPriceAfterVat != null && <small className="table-subtext">PO sau VAT: {formatMoney(line.customerUnitPriceAfterVat)}</small>}</td>
                        <td><select value={line.vatPercent} onChange={(e) => updateLine(index, { vatPercent: Number(e.target.value) })}><option value="0">Không VAT</option><option value="5">5%</option><option value="8">8%</option><option value="10">10%</option></select></td>
                        <td className="number-cell"><strong>{formatMoney(lineTotal)}</strong></td>
                        <td><button className="remove-icon" disabled={form.lines.length === 1} onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== index) })}><Trash2 size={15} /></button></td>
                      </tr>
                      {type === "SalesOrder" && line.customerIdentityFromPo && <tr className="allocation-detail-row"><td /><td colSpan={7}>
                        <div className="allocation-editor">
                          <div className="allocation-heading"><strong>Phân bổ sản phẩm nội bộ</strong><span className={line.allocations.length === 0 ? "status-pill info" : allocationComplete ? "status-pill success" : "status-pill danger"}>{line.allocations.length === 0 ? "Có thể để đến lúc xuất kho" : `Đã phân bổ ${allocated} / ${line.quantity}`}</span></div>
                          {line.allocations.map((allocation, allocationIndex) => <div className="allocation-line" key={allocation.clientKey}>
                            <span>{allocationIndex + 1}</span>
                            <SearchSelect value={allocation.itemId} options={itemSearchOptions} onChange={(itemId) => updateAllocation(index, allocationIndex, { itemId })} placeholder="Chọn mã nội bộ thực tế" />
                            <input value={allocation.customerVariantName} onChange={(event) => updateAllocation(index, allocationIndex, { customerVariantName: event.target.value })} placeholder="Tên phân loại: Doremon, Kitty..." />
                            <input type="number" min="0" step="any" value={allocation.quantity} onChange={(event) => updateAllocation(index, allocationIndex, { quantity: Number(event.target.value) })} />
                            <span>{uomSearchOptions.find((option) => option.value === line.uomId)?.selectedLabel ?? "ĐVT"}</span>
                            <button className="remove-icon" onClick={() => removeAllocation(index, allocationIndex)}><Trash2 size={15} /></button>
                          </div>)}
                          <button className="secondary-button compact-button" onClick={() => addAllocation(index)}><Plus size={15} /> Phân bổ sản phẩm nội bộ</button>
                        </div>
                      </td></tr>}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="add-line-below"><button className="secondary-button" onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })}><Plus size={15} /> Thêm dòng</button></div>
          </div>
          <div className="document-total">
            <span>Giá trị hàng: <strong>{formatMoney(total - vatTotal)}</strong></span>
            <span>VAT: <strong>{formatMoney(vatTotal)}</strong></span>
            <span className="grand-total">Tổng giá: <strong>{formatMoney(total)}</strong></span>
          </div>
          <label className="full-field">Ghi chú<textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <div className="form-actions"><button className="secondary-button" onClick={() => setShowForm(false)}>Đóng</button><button className="primary-button" onClick={() => void save()}>{editingId ? "Lưu thay đổi" : "Lưu bản Nháp"}</button></div>
        </Modal>
      )}

      {partnerPrompt && (
        <Modal
          title={`${type === "PurchaseOrder" ? "Nhà cung cấp" : "Khách hàng"} chưa có trong danh mục`}
          subtitle={`${type === "PurchaseOrder" ? "Nhà cung cấp" : "Khách hàng"} “${form.partnerName.trim()}” chưa có trong danh mục. Anh có cần lưu ${type === "PurchaseOrder" ? "nhà cung cấp" : "khách hàng"} này vào danh mục không?`}
          onClose={() => setPartnerPrompt(false)}
          error={error}
        >
          {partnerOptions.filter((partner) => {
            const entered = form.partnerName.trim().toLocaleLowerCase("vi-VN");
            const current = partner.name.toLocaleLowerCase("vi-VN");
            return entered && (current.includes(entered) || entered.includes(current));
          }).slice(0, 3).map((partner) => <p key={partner.id}>Tên gần giống đã có: <strong>{partner.code} – {partner.name}</strong></p>)}
          <div className="form-actions">
            <button className="secondary-button" onClick={() => setPartnerPrompt(false)}>Đóng</button>
            <button className="secondary-button" onClick={() => void persist(false)}>Không, chỉ dùng cho đơn này</button>
            <button className="primary-button" onClick={() => void persist(true)}>Có, lưu vào danh mục</button>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={detail.number} subtitle={`${titleByType[detail.documentType]} – ${detail.partner.name}`} onClose={() => setDetail(null)} wide>
          <div className="document-overview"><span>Ngày: <strong>{formatDate(detail.documentDate)}</strong></span><span>Trạng thái: {workflowBadge(detail)}</span><span>Tổng giá: <strong>{formatMoney(detail.grandTotal)}</strong></span>{isOrderType(detail.documentType) && <span>Kho: <strong>{fulfillmentLabel(detail)}</strong></span>}{detail.customerPoNumber && <span>PO khách: <strong>{detail.customerPoNumber}</strong></span>}{detail.deliveryAddress && <span>Giao tại: <strong>{detail.deliveryAddress}</strong></span>}</div>
          {detail.customerPoAttachmentName && <div className="form-actions compact"><button className="secondary-button" onClick={() => void downloadCustomerPo(detail)}><Download size={16} /> Tải PO gốc: {detail.customerPoAttachmentName}</button></div>}
          <div className="data-table-wrapper"><table className="data-table"><thead><tr><th>STT</th><th>Mã</th><th>Tên hàng</th><th>ĐVT</th><th>SL đặt</th>{isOrderType(detail.documentType) && <><th>{detail.documentType === "PurchaseOrder" ? "Đã nhập" : "Đã xuất"}</th><th>Còn nợ</th></>}<th>Đơn giá</th><th>VAT</th><th>Thành giá</th></tr></thead><tbody>{detail.lines.map((line) => { const processed = line.quantityProcessed ?? line.quantityIssued ?? line.quantityReceived ?? 0; const remaining = line.quantityRemaining ?? line.quantity; return <tr className={isOrderType(detail.documentType) ? fulfillmentProgressClass(line.quantity, processed, remaining) : undefined} key={line.id}><td>{line.lineNo}</td><td><strong>{line.itemCode}</strong>{line.internalItemCode && line.internalItemCode !== line.itemCode && <small className="table-subtext">Nội bộ: {line.internalItemCode}</small>}</td><td>{line.itemName}{line.internalItemName && line.internalItemName !== line.itemName && <small className="table-subtext">Nội bộ: {line.internalItemName}</small>}{line.allocations?.map((allocation) => <small className="table-subtext allocation-summary" key={allocation.id}><strong>{allocation.itemCode}</strong> – {allocation.itemName} ({allocation.customerVariantName}): {allocation.quantity} {allocation.uomName}</small>)}</td><td>{line.uomName}</td><td>{line.quantity}</td>{isOrderType(detail.documentType) && <><td>{processed}</td><td><strong>{remaining}</strong></td></>}<td>{formatMoney(line.unitPrice)}</td><td>{line.vatPercent}%</td><td><strong>{formatMoney(line.lineTotal)}</strong></td></tr>; })}</tbody></table></div>
          {isOrderType(detail.documentType) && <div className="line-editor"><div className="line-editor-header"><strong>{detail.documentType === "PurchaseOrder" ? "Phiếu nhập kho liên quan" : "Phiếu xuất kho và Phiếu giao hàng liên quan"}</strong></div><div className="data-table-wrapper"><table className="data-table"><thead><tr><th className="stt-column">STT</th><th>Phiếu kho</th><th>Ngày</th><th>Trạng thái</th><th>Phiếu giao hàng</th></tr></thead><tbody>{detail.linkedInventoryDocuments?.map((document, index) => <tr key={document.id}><td className="stt-column">{index + 1}</td><td><strong>{document.number}</strong></td><td>{formatDate(document.documentDate)}</td><td><StatusBadge status={document.status} /></td><td>{document.deliveryNote?.number ?? "—"}</td></tr>)}{!detail.linkedInventoryDocuments?.length && <tr><td colSpan={5}><div className="empty-state">Chưa có phiếu kho.</div></td></tr>}</tbody></table></div></div>}
        </Modal>
      )}
    </div>
  );
}
